import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

import useModal from '@/shared/hooks/useModal'
import { selectAuthData } from '@/containers/Login/AuthSelectors'

import { teamsManagementActions } from './TeamsManagementSagas'
import { selectTeamsManagement } from './TeamsManagementSelectors'
import { updateTeamsManagementSelectedTeam } from './TeamsManagementSlice'
import { SelectedTeam, Team, TeamsRoutes } from './types'

interface UseTeamsManagementReturn {
  teams: Team[]
  selectedTeam: SelectedTeam
  teamsListIsLoading: boolean
  teamsError: unknown
  canDelete: boolean
  t: TFunction
  isOpen: boolean
  close: () => void
  setIsOpen: (open: boolean) => void
  updateTeam: (team: Team | null) => void
  deleteTeam: (teamId: number | string) => void
}

const useTeamsManagement = (): UseTeamsManagementReturn => {
  const dispatch = useDispatch()
  const { extraRoutes } = useSelector(selectAuthData) as { extraRoutes?: { teams?: TeamsRoutes } }
  const teamsRoutes: TeamsRoutes = extraRoutes?.teams ?? {}

  const { teams, selectedTeam, isLoadingTeams, teamsError } = useSelector(selectTeamsManagement)

  const { t } = useTranslation()
  const { isOpen, close, setIsOpen } = useModal()

  const updateTeam = (team: Team | null) => {
    const teamSelected: SelectedTeam = {
      id: team?.id ?? 0,
      name: team?.id ? team.name : '',
      site_id: team?.id ? team.site_id ?? '' : '',
    }
    dispatch(updateTeamsManagementSelectedTeam(teamSelected))
    setIsOpen(true)
  }

  const deleteTeam = (teamId: number | string) => {
    dispatch({ type: teamsManagementActions.DELETE_TEAM, teamId })
  }

  useEffect(() => {
    if (!teamsRoutes.list) return
    dispatch({ type: teamsManagementActions.LOAD_LIST })
  }, [teamsRoutes.list])

  return {
    teams,
    selectedTeam,
    teamsListIsLoading: isLoadingTeams,
    teamsError,
    canDelete: !!teamsRoutes.delete,
    t,
    isOpen,
    close,
    setIsOpen,
    updateTeam,
    deleteTeam,
  }
}

export default useTeamsManagement
